import { yupResolver } from '@hookform/resolvers/yup';
import { DoctorType, QueryFactory } from '@lmt/shared-types';
import {
  CustomDictionaryName,
  useActiveDictionaryValuesByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { useActiveReferringDoctors } from '@lmt/unicorn-shared/app/components/doctors/doctor-form/common/common';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { EditPatientSubmenu } from '@lmt/unicorn-shared/app/components/patients/patient-form/components/EditPatientSubmenu';
import { ImportErrorsMessage } from '@lmt/unicorn-shared/app/components/patients/patient-form/components/ImportErrorsMessage';
import { NavigationActions } from '@lmt/unicorn-shared/app/features/navigation/reducer';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useResetFormWhenDataIsLoaded } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';
import {
  handleSubmitFormError,
  useErrorHandler,
} from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import {
  getContactFieldValue,
  getFullName,
  getPriorityContactFieldValue,
  patientDataYupSchema,
  titleKey,
} from './common/common';
import { PatientData } from './common/types';
import { UpdateContactWhenFieldsChange } from './common/UpdateContactsOnFieldsChange';
import { ExpandedPatientForm } from './components/ExpandedPatientForm';

import styles from './CreatePatient.module.scss';
import { useGlobalForms } from '../../global-forms/useGlobalForms';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';

export interface EditPatientProps {
  patientId: number;
}

const nameof = nameofFactory<PatientData>();

export const EditPatient: FC<FormComponentProps & EditPatientProps> = (
  props,
) => {
  const {
    formType,
    formId,
    tryCompleteForm,
    patientId,
    registerFormDirtyStateRetriever,
    loadBlocking,
  } = props;
  const i18next = useScopedTranslation('patients.form.edit_patient');
  const dispatch = useUniDispatch();
  const modals = useModal();
  const queryClient = useQueryClient();
  const globalForms = useGlobalForms();

  const [doctorsSearchQuery, setDoctorsSearchQuery] = useState<string | null>(
    null,
  );
  const [isCustomFieldsValid, setIsCustomFieldsValid] = useState(true);
  const [customFieldsDirty, setCustomFieldsDirty] = useState(false);
  const [isCustomFieldsErrorsShown, setCustomFieldErrorsShown] =
    useState<boolean>(false);
  const [searchQueryDebounced] = useDebounce(doctorsSearchQuery, 100);

  const schema = useMemo(patientDataYupSchema, []);
  const form = useForm<PatientData>({
    resolver: yupResolver(schema),
    shouldUnregister: false,
    // for some reason we need to set default values for <Controller> components
    defaultValues: {
      sex: null!,
      dateOfBirth: null!,
      dateOfDeath: null!,
      doctorId: null!,
      subtenantId: null!,
      healthInsuranceCompanyId: null!,
      values: {},
    },
  });
  const { handleSubmit, setError } = form;

  useEffect(() => {
    registerFormDirtyStateRetriever(
      formId,
      () =>
        Object.keys(form.formState.dirtyFields).length > 0 || customFieldsDirty,
    );
  }, [form.formState.dirtyFields, customFieldsDirty]);

  const patientQuery = QueryFactory.PatientQuery.useGetPatientQuery(patientId);
  const { data: personTitles } = useActiveDictionaryValuesByTitle(
    CustomDictionaryName.PersonTitles,
    patientQuery?.data?.values?.[titleKey],
  );
  const patientData = useMemo(
    () =>
      patientQuery.data && {
        ...patientQuery.data,
        contact: getContactFieldValue(patientQuery.data),
        fullName: getFullName(patientQuery.data),
        priorityContact: getPriorityContactFieldValue(patientQuery.data) || '',
      },
    [patientQuery.data],
  );
  useResetFormWhenDataIsLoaded(form, patientData);

  const currentDoctorId = form.watch(nameof('doctorId'));
  const doctors = useActiveReferringDoctors(
    searchQueryDebounced,
    currentDoctorId,
  );

  const reasonForRemoving = patientQuery.data?.isDeleted && (
    <div className={styles.reasonContainer}>
      <div className={styles.reasonTitle}>
        {patientQuery.data.anonymous
          ? i18next.t('anonymize_reason_title')
          : i18next.t('archive_reason_title')}
      </div>
      <div className={styles.reasonTextContainer}>
        <div className={styles.reasonText} data-test-id="deletion-reason">
          {patientQuery.data.deletionReason}
        </div>
      </div>
    </div>
  );

  const titleDictionaryItem = useMemo(() => {
    const titleId = patientData?.values?.[titleKey];
    if (!titleId || Number.isNaN(Number(titleId))) {
      return null;
    }
    return personTitles.find((x) => x.id === titleId);
  }, [patientData, personTitles]);

  // to save all changes in UIEditor
  const beforeSaveRef = useRef<() => Promise<void>>(null);
  const submitForm = useErrorHandler(
    useCallback(
      async (data: PatientData) => {
        if (!isCustomFieldsValid) {
          return;
        }
        try {
          await tryCompleteForm(
            formType,
            formId,
            async () => {
              // -----------------
              // When changing this code make sure to change the similar one in CreatePatient.tsx!!!
              // -----------------
              if (beforeSaveRef.current) {
                data.values = await beforeSaveRef.current();
              }
              // data.customFields only contains CHANGED fields. So we need to apply them above other predefined customFields
              const customValues = {
                ...patientData?.values,
                ...data.values,
                'Health insurance company': data.healthInsuranceCompanyId,
                'Family doctor': data.doctorId,
              };
              if (titleDictionaryItem?.isDeleted) {
                // we can't save patient with deleted dictionary item, so we replace it with string value
                customValues[titleKey] = titleDictionaryItem.value;
              }
              const patient = {
                firstName: data.firstName,
                lastName: data.lastName,
                dateOfBirth: data.dateOfBirth || undefined,
                sex: data.sex,
                dateOfDeath: data.isDeceased ? data.dateOfDeath : null!,
                isDeceased: data.isDeceased,
                mobilePhone: data.mobilePhone,
                email: data.email,
                emailsAllowed: data.emailsAllowed,
                doctorId: data.doctorId,
                address: data.address,
                location: data.location,
                postcode: data.postcode,
                homePhone: data.homePhone,
                workPhone: data.workPhone,
                fax: data.fax,
                number: data.number,
                healthInsuranceCompanyId: data.healthInsuranceCompanyId,
                isPrivateInsurance: data.isPrivateInsurance,
                isSelfPayer: data.isSelfPayer,
                insuranceNumber: data.insuranceNumber,
                values: customValues,
                priorityContactType: data.priorityContactType,
                comments: data.comments,
                importErrors: null, // we reset import errors on patient update, assuming that patient checked these errors
              };
              const updatedPatient =
                await QueryFactory.PatientClient.editPatient(
                  patientId,
                  patient,
                );
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.PatientDuplicateQuery.getQueryKey(),
              });
              const patientTitle = getFullName(patient);
              dispatch(
                NavigationActions.patientChanged({
                  patientId: patientId,
                  title: patientTitle,
                }),
              );
            },
            i18next.t('update_loading'),
          );
        } catch (err) {
          const errorDetails = handleSubmitFormError<PatientData>(
            err,
            form.setError,
          );
          if (errorDetails.overallServerError) {
            await modals.showError({ text: errorDetails.overallServerError });
          }
        }
      },
      [
        form,
        formType,
        formId,
        tryCompleteForm,
        patientData,
        isCustomFieldsValid,
        titleDictionaryItem,
      ],
    ),
    setError,
  );

  const addNewDoctor = useCallback(async () => {
    const createdDoctor = await globalForms.openDoctorCreating(
      DoctorType.External,
    );
    if (createdDoctor) {
      queryClient.setQueryData(QueryFactory.DoctorQuery.getDoctorsQueryKey(), {
        data: doctors.concat({
          ...createdDoctor,
        }),
      });
      form.setValue('doctorId', createdDoctor.id, validateAndDirty);
    }
  }, [globalForms, form.setValue]);

  return (
    <Loading loading={patientQuery.isLoading} title={i18next.t('data_loading')}>
      <form
        className={styles.expandedFormContainer}
        onSubmit={handleSubmit(submitForm.handler)}
        data-test-id="editPatientPopup"
      >
        <UpdateContactWhenFieldsChange form={form} />
        <div className={styles.dataContainer}>
          <div className={styles.scrollableContainer}>
            <ExpandedPatientForm
              form={form}
              isSubtenantEnabled={false}
              isDeceasedEnabled={true}
              doctors={doctors}
              addNewDoctor={addNewDoctor}
              subtenants={[]}
              onIsValidChange={setIsCustomFieldsValid}
              beforeSaveRef={beforeSaveRef}
              onDoctorsSearch={setDoctorsSearchQuery}
              showCustomFieldsErrors={isCustomFieldsErrorsShown}
              onDirtyChange={setCustomFieldsDirty}
            />
          </div>
          <div>
            {reasonForRemoving}
            {patientData?.importErrors && (
              <ImportErrorsMessage importErrors={patientData.importErrors} />
            )}
            <div className={styles.submitContainer}>
              <EditPatientSubmenu
                patientId={props.patientId}
                loadBlocking={loadBlocking}
                tryCompleteForm={tryCompleteForm}
                formId={formId}
                formType={formType}
              />

              <SubmitButton
                submitting={form.formState.isSubmitting}
                className={styles.submit}
                title={i18next.t('save_button')}
                testId={'editPatient-saveButton'}
                onClick={() => setCustomFieldErrorsShown(true)}
              />
            </div>
          </div>
        </div>
      </form>
    </Loading>
  );
};
